import { ReactNode, Dispatch, SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { VaultKeys } from '../../../api/vault-api';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import SharedGridStateIconButton from '../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../shared/analytics/module-list';
import { getDeleteMatterPermissionState } from '../../../utils/vault-utils';
import { VaultRoles } from '../../../utils/roles/client-roles';

import SharedButton from '../../../shared/buttons/shared-button';
import Tooltip from '@mui/material/Tooltip';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Add from '@mui/icons-material/Add';
import ViewModule from '@mui/icons-material/ViewModule';
import TableRows from '@mui/icons-material/TableRows';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';

export interface VaultMattersGridToolbarProps {
  children?: ReactNode;
  toolsRoles: string[];
  selectedMatters: string[];
  setOpenDeleteMatterDialog: Dispatch<SetStateAction<boolean>>;
  onCreateMatter: () => void;
  matterView: 'tile' | 'table';
  setMatterView: Dispatch<SetStateAction<'tile' | 'table'>>;
  canCreateMatter?: boolean;
}

export default function VaultMattersGridToolbar({
  children,
  toolsRoles,
  selectedMatters,
  setOpenDeleteMatterDialog,
  onCreateMatter,
  matterView,
  setMatterView,
  canCreateMatter,
}: Readonly<VaultMattersGridToolbarProps>) {
  const queryClient = useQueryClient();

  const deleteState = getDeleteMatterPermissionState(toolsRoles);

  const canDelete = deleteState.canDelete && selectedMatters.length > 0;

  const deleteTooltip = deleteState.canDelete ? 'Delete' : deleteState.tooltip;

  return (
    <SharedToolbar quickFilterPlaceholder="Search...">
      <SharedIconButton
        variant="delete"
        onClick={() => {
          if (selectedMatters.length > 0) {
            setOpenDeleteMatterDialog(true);
          }
        }}
        disabled={!canDelete}
        tooltipLabel={deleteTooltip}
      />

      <SharedIconButton
        variant="refresh"
        onClick={() => {
          queryClient.invalidateQueries({
            queryKey: [VaultKeys.VaultMatters],
          });
        }}
        tooltipLabel="Refresh"
      />

      <SharedGridStateIconButton
        gridStateKey="vaultMattersGridStates"
        module={ModuleList.modules.importExport.name}
        section={ModuleList.modules.importExport.sections.vaultExport}
      />

      <Tooltip
        arrow
        title={
          !canCreateMatter
            ? `You do not have the "${VaultRoles.CreateVaultMatters.name}" permission.`
            : ''
        }
      >
        <span>
          <SharedButton
            startIcon={<Add />}
            variant="primary"
            disabled={!canCreateMatter}
            onClick={onCreateMatter}
            sx={{ ml: 1 }}
          >
            Create Matter
          </SharedButton>
        </span>
      </Tooltip>

      <ToggleButtonGroup
        value={matterView}
        exclusive
        onChange={(_, value) => value && setMatterView(value)}
        size="small"
        sx={{ ml: 1 }}
      >
        <ToggleButton value="tile" aria-label="Tile view">
          <ViewModule />
        </ToggleButton>
        <ToggleButton value="table" aria-label="Table view">
          <TableRows />
        </ToggleButton>
      </ToggleButtonGroup>

      {children}
    </SharedToolbar>
  );
}
